import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBrain,
  faPlus,
  faTrash,
  faCheck,
  faExclamationTriangle,
  faBell,
  faSpinner,
  faCalendarDay,
  faFaceSmile,
  faPhone,
  faMessage,
  faHandHoldingHeart,
  faTriangleExclamation,
  faArrowRight,
  faFlag,
  faClipboardList,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import womensHealthService, {
  PostpartumProfile,
  MoodLog,
  EPDSQuestion,
} from '../../../services/womensHealthService';
import BackButton from '../../../components/BackButton/BackButton';
import WHArticleList from '../../../components/WHArticleList/WHArticleList';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import ProfileStrip from '../../../components/ProfileStrip/ProfileStrip';
import { getApiErrorMessage } from '../../../api/axiosInstance';
import './PostpartumMood.css';
import { useProfile } from '../../../context/ProfileContext';

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

type TabKey = 'checkin' | 'history' | 'resources';

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const formatDate = (iso: string | null): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const todayStr = (): string => new Date().toISOString().split('T')[0];

const getMoodEmoji = (score: number): string => {
  if (score <= 2) return '\uD83D\uDE2D';
  if (score <= 4) return '\uD83D\uDE1F';
  if (score <= 6) return '\uD83D\uDE10';
  if (score <= 8) return '\uD83D\uDE0A';
  return '\uD83D\uDE04';
};

const getMoodLabel = (score: number): string => {
  if (score <= 2) return 'Very Low';
  if (score <= 4) return 'Low';
  if (score <= 6) return 'Neutral';
  if (score <= 8) return 'Good';
  return 'Excellent';
};

const getMoodColor = (score: number): string => {
  if (score <= 2) return '#ef4444';
  if (score <= 4) return '#f97316';
  if (score <= 6) return '#eab308';
  if (score <= 8) return '#22c55e';
  return '#10b981';
};

/* ================================================================== */
/*  Toast                                                              */
/* ================================================================== */

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const iconMap: Record<string, IconDefinition> = {
    success: faCheck,
    error: faExclamationTriangle,
    info: faBell,
  };

  return (
    <div className={`wh-mood-toast wh-mood-toast-${type}`}>
      <span className="wh-mood-toast-icon">
        <FontAwesomeIcon icon={iconMap[type]} />
      </span>
      <span className="wh-mood-toast-message">{message}</span>
      <button className="wh-mood-toast-close" onClick={onClose} aria-label="Close notification">
        {'\u00D7'}
      </button>
    </div>
  );
};

/* ================================================================== */
/*  Skeleton                                                           */
/* ================================================================== */

const SkeletonCards: React.FC = () => (
  <div className="wh-mood-skeleton-grid">
    {[0, 1, 2].map((i) => (
      <div key={i} className="wh-mood-skeleton-card">
        <div className="wh-mood-skeleton-line long" />
        <div className="wh-mood-skeleton-line medium" />
        <div className="wh-mood-skeleton-line short" />
      </div>
    ))}
  </div>
);

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

const PostpartumMood: React.FC = () => {
  const { t } = useTranslation(['womensHealth', 'common']);
  const { activeProfile } = useProfile();
  const [searchParams] = useSearchParams();

  // Data
  const [profile, setProfile] = useState<PostpartumProfile | null>(null);
  const [moodLogs, setMoodLogs] = useState<MoodLog[]>([]);
  const [flaggedLogs, setFlaggedLogs] = useState<MoodLog[]>([]);
  const [screeningQuestions, setScreeningQuestions] = useState<EPDSQuestion[]>([]);

  // UI
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>((searchParams.get('tab') as TabKey) || 'checkin');
  const [toast, setToast] = useState<ToastState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [showCrisisAlert, setShowCrisisAlert] = useState(false);

  // Form
  const [moodScore, setMoodScore] = useState(5);
  const [screeningAnswers, setScreeningAnswers] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState('');

  // Mood resources (from WHContent: postpartum/mood_resources)
  const showToast = useCallback((message: string, type: ToastState['type']) => {
    setToast({ message, type });
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Data Fetching                                                    */
  /* ---------------------------------------------------------------- */

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const pp = await womensHealthService.getActivePostpartum();
      setProfile(pp);

      if (pp) {
        const [logs, flagged] = await Promise.all([
          womensHealthService.getMoodLogs(pp.id),
          womensHealthService.getFlaggedMoodLogs(pp.id),
        ]);
        setMoodLogs(Array.isArray(logs) ? logs : []);
        setFlaggedLogs(Array.isArray(flagged) ? flagged : []);
      }
    } catch (err) {
      console.error('Failed to fetch mood data:', err);
      showToast('Failed to load mood data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData, activeProfile]);

  useEffect(() => {
    let cancelled = false;
    womensHealthService.getMoodScreeningQuestions()
      .then((qs) => { if (!cancelled) setScreeningQuestions(qs); })
      .catch((err) => { console.error('Failed to load EPDS questions:', err); });
    return () => { cancelled = true; };
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Submit Mood Check-in                                             */
  /* ---------------------------------------------------------------- */

  const handleSubmit = async () => {
    const total = screeningQuestions.length;
    const answeredCount = Object.keys(screeningAnswers).length;
    if (total === 0) {
      showToast('Screening questions are still loading. Please try again in a moment.', 'error');
      return;
    }
    if (answeredCount < total) {
      showToast(`Please answer all ${total} screening questions. (${answeredCount}/${total} answered)`, 'error');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        postpartum: profile!.id,
        date: todayStr(),
        mood_score: moodScore,
        screening_answers: screeningAnswers,
        notes: notes.trim(),
      };
      const result = await womensHealthService.createMoodLog(payload);

      if (result.flagged) {
        setShowCrisisAlert(true);
      }

      showToast('Mood check-in saved successfully!', 'success');
      setMoodScore(5);
      setScreeningAnswers({});
      setNotes('');
      await fetchData();
      setActiveTab('history');
    } catch (err) {
      console.error('Error saving mood log:', err);
      showToast(getApiErrorMessage(err, 'Failed to save mood check-in.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Delete                                                           */
  /* ---------------------------------------------------------------- */

  const handleDelete = async () => {
    if (confirmDelete === null) return;
    try {
      await womensHealthService.deleteMoodLog(confirmDelete);
      showToast('Mood log deleted.', 'success');
      setConfirmDelete(null);
      await fetchData();
    } catch (err) {
      console.error('Error deleting mood log:', err);
      showToast('Failed to delete mood log.', 'error');
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Tab Config                                                       */
  /* ---------------------------------------------------------------- */

  const tabs: { key: TabKey; label: string; icon: IconDefinition }[] = [
    { key: 'checkin', label: t('womensHealth:postpartum.mood.moodTracking'), icon: faFaceSmile },
    { key: 'history', label: t('womensHealth:postpartum.mood.screeningQuiz'), icon: faClipboardList },
    { key: 'resources', label: t('womensHealth:postpartum.mood.resources'), icon: faHandHoldingHeart },
  ];

  /* ================================================================ */
  /*  Render: No Profile                                               */
  /* ================================================================ */

  const renderNoProfile = () => (
    <div className="wh-mood-no-profile">
      <div className="wh-mood-no-profile-icon">
        <FontAwesomeIcon icon={faBrain} />
      </div>
      <h2>Set Up Your Postpartum Profile First</h2>
      <p>
        To start tracking your mood and mental wellbeing, you need to create your postpartum recovery profile.
      </p>
      <a href="/womens-health/postpartum-recovery" className="wh-mood-btn wh-mood-btn-primary">
        <FontAwesomeIcon icon={faArrowRight} /> Go to Postpartum Recovery
      </a>
    </div>
  );

  /* ================================================================ */
  /*  Render: Flagged Warning Banner                                   */
  /* ================================================================ */

  const renderFlaggedWarning = () => {
    if (flaggedLogs.length === 0) return null;
    return (
      <div className="wh-mood-flagged-banner">
        <div className="wh-mood-flagged-icon">
          <FontAwesomeIcon icon={faTriangleExclamation} />
        </div>
        <div className="wh-mood-flagged-text">
          <strong>Attention: {flaggedLogs.length} flagged mood {flaggedLogs.length === 1 ? 'entry' : 'entries'} detected</strong>
          <p>Some of your recent mood screenings indicate you may benefit from professional support. Please reach out to a healthcare provider or contact the crisis resources below.</p>
        </div>
      </div>
    );
  };

  /* ================================================================ */
  /*  Render: Crisis Alert                                             */
  /* ================================================================ */

  const renderCrisisAlert = () => {
    if (!showCrisisAlert) return null;
    return (
      <div className="wh-mood-modal-overlay" onClick={() => setShowCrisisAlert(false)}>
        <div className="wh-mood-crisis-modal" onClick={(e) => e.stopPropagation()}>
          <div className="wh-mood-crisis-header">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <h3>Your screening results may need attention</h3>
          </div>
          <p>
            Your responses suggest you may be experiencing symptoms of postpartum depression or anxiety.
            This is more common than you might think, and help is available. You are not alone.
          </p>
          <div className="wh-mood-crisis-resources">
            <div className="wh-mood-crisis-item">
              <FontAwesomeIcon icon={faPhone} />
              <div>
                <strong>National Postpartum Support Helpline</strong>
                <span>1-800-944-4773</span>
              </div>
            </div>
            <div className="wh-mood-crisis-item">
              <FontAwesomeIcon icon={faMessage} />
              <div>
                <strong>Crisis Text Line</strong>
                <span>Text HOME to 741741</span>
              </div>
            </div>
          </div>
          <button className="wh-mood-btn wh-mood-btn-primary" onClick={() => setShowCrisisAlert(false)}>
            I Understand
          </button>
        </div>
      </div>
    );
  };

  /* ================================================================ */
  /*  Render: Mood Check-in Tab                                        */
  /* ================================================================ */

  const renderCheckinTab = () => (
    <div className="wh-mood-tab-panel">
      {/* Mood Score Slider */}
      <div className="wh-mood-score-card">
        <h3 className="wh-mood-score-title">How are you feeling today?</h3>
        <div className="wh-mood-emoji-display">
          <span className="wh-mood-emoji">{getMoodEmoji(moodScore)}</span>
          <span className="wh-mood-score-label" style={{ color: getMoodColor(moodScore) }}>
            {moodScore}/10 - {getMoodLabel(moodScore)}
          </span>
        </div>
        <div className="wh-mood-slider-wrap">
          <span className="wh-mood-slider-end">{'\uD83D\uDE2D'}</span>
          <input
            type="range"
            min={1}
            max={10}
            value={moodScore}
            onChange={(e) => setMoodScore(parseInt(e.target.value))}
            className="wh-mood-slider"
            style={{
              background: `linear-gradient(to right, ${getMoodColor(moodScore)} 0%, ${getMoodColor(moodScore)} ${((moodScore - 1) / 9) * 100}%, #e2e8f0 ${((moodScore - 1) / 9) * 100}%, #e2e8f0 100%)`,
            }}
          />
          <span className="wh-mood-slider-end">{'\uD83D\uDE04'}</span>
        </div>
      </div>

      {/* EPDS Screening */}
      <div className="wh-mood-screening-section">
        <div className="wh-mood-screening-header">
          <h3>Edinburgh Postnatal Depression Scale (EPDS)</h3>
          <p>Please select the answer that comes closest to how you have felt in the past 7 days.</p>
        </div>

        <div className="wh-mood-questions-list">
          {screeningQuestions.map((q, idx) => (
            <div key={q.id} className="wh-mood-question-card">
              <div className="wh-mood-question-number">{idx + 1}</div>
              <div className="wh-mood-question-content">
                <p className="wh-mood-question-text">{q.text}</p>
                <div className="wh-mood-question-options">
                  {q.options.map((opt) => (
                    <label
                      key={opt.value}
                      className={`wh-mood-radio-label ${screeningAnswers[q.id] === opt.value ? 'active' : ''}`}
                    >
                      <input
                        type="radio"
                        name={q.id}
                        value={opt.value}
                        checked={screeningAnswers[q.id] === opt.value}
                        onChange={() => setScreeningAnswers((prev) => ({ ...prev, [q.id]: opt.value }))}
                      />
                      <span className="wh-mood-radio-custom" />
                      <span className="wh-mood-radio-text">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="wh-mood-notes-section">
        <label className="wh-mood-form-label">Additional Notes</label>
        <textarea
          className="wh-mood-form-textarea"
          rows={3}
          placeholder="Any thoughts, feelings, or observations you want to record..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      {/* Submit */}
      <div className="wh-mood-submit-section">
        <button
          className="wh-mood-btn wh-mood-btn-primary wh-mood-btn-lg"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? (
            <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
          ) : (
            <><FontAwesomeIcon icon={faCheck} /> {t('common:buttons.submit')}</>
          )}
        </button>
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Render: History Tab                                              */
  /* ================================================================ */

  const renderHistoryTab = () => (
    <div className="wh-mood-tab-panel">
      {loading ? (
        <SkeletonCards />
      ) : moodLogs.length === 0 ? (
        <div className="wh-mood-empty-state">
          <div className="wh-mood-empty-icon-wrap">
            <FontAwesomeIcon icon={faFaceSmile} />
          </div>
          <h3>No mood logs yet</h3>
          <p>Complete your first mood check-in to start tracking your emotional wellbeing.</p>
          <button
            className="wh-mood-btn wh-mood-btn-primary"
            onClick={() => setActiveTab('checkin')}
          >
            <FontAwesomeIcon icon={faPlus} /> Start Check-in
          </button>
        </div>
      ) : (
        <div className="wh-mood-history-grid">
          {moodLogs.map((log) => {
            const color = getMoodColor(log.mood_score);
            return (
              <div key={log.id} className="wh-mood-history-card" style={{ borderTopColor: color }}>
                <div className="wh-mood-history-header">
                  <span className="wh-mood-history-date">
                    <FontAwesomeIcon icon={faCalendarDay} /> {formatDate(log.date)}
                  </span>
                  {log.flagged && (
                    <span className="wh-mood-flagged-chip">
                      <FontAwesomeIcon icon={faFlag} /> Flagged
                    </span>
                  )}
                </div>
                <div className="wh-mood-history-score">
                  <span className="wh-mood-history-emoji">{getMoodEmoji(log.mood_score)}</span>
                  <div className="wh-mood-history-score-info">
                    <span className="wh-mood-history-score-value" style={{ color }}>{log.mood_score}/10</span>
                    <span className="wh-mood-history-score-label">{getMoodLabel(log.mood_score)}</span>
                  </div>
                </div>
                {log.screening_total !== null && (
                  <div className="wh-mood-history-screening">
                    <span className="wh-mood-screening-badge">
                      EPDS Score: {log.screening_total}/30
                    </span>
                  </div>
                )}
                {log.notes && (
                  <p className="wh-mood-history-notes">{log.notes}</p>
                )}
                <div className="wh-mood-history-actions">
                  <button
                    className="wh-mood-action-btn wh-mood-action-delete"
                    onClick={() => setConfirmDelete(log.id)}
                  >
                    <FontAwesomeIcon icon={faTrash} /> {t('common:buttons.delete')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  /* ================================================================ */
  /*  Render: Support Resources Tab                                    */
  /* ================================================================ */

  const renderResourcesTab = () => (
    <div className="wh-mood-tab-panel">
      <WHArticleList
        stage="postpartum"
        contentType="mood_resources"
        variant="accordion"
        icon={faHandHoldingHeart}
        accentColor="#7c3aed"
      />
    </div>
  );

  /* ================================================================ */
  /*  Main Render                                                      */
  /* ================================================================ */

  return (
    <div className="wh-mood-page">
      {/* Toast */}
      {toast && (
        <div className="wh-mood-toast-container">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      {/* Crisis Alert */}
      {renderCrisisAlert()}

      {/* Delete Modal */}
      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Mood Entry?"
        message="This action cannot be undone."
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(null)}
      />

      {/* Hero Banner */}
      <section className="wh-mood-hero">
        <div className="wh-mood-hero-circle wh-mood-hero-circle-1" />
        <div className="wh-mood-hero-circle wh-mood-hero-circle-2" />
        <Container>
          <BackButton />
          <div className="wh-mood-hero-content">
            <div className="wh-mood-hero-badge">
              <FontAwesomeIcon icon={faBrain} />
              Postpartum
            </div>
            <h1 className="wh-mood-hero-title">{t('womensHealth:postpartum.mood.title')}</h1>
            <p className="wh-mood-hero-subtitle">
              Track your mood, complete the EPDS screening, and access support resources for your emotional health journey.
            </p>
          </div>
        </Container>
      </section>

      {/* Content */}
      <section className="wh-mood-content-section">
        <Container>
          {loading ? (
            <SkeletonCards />
          ) : !profile ? (
            renderNoProfile()
          ) : (
            <>
              {renderFlaggedWarning()}

              <ProfileStrip
                summary={`Delivery ${formatDate(profile.delivery_date)} · ${profile.recovery_mode_display || profile.recovery_mode || 'Recovery'}`}
                hubPath="/womens-health/postpartum-recovery"
                hubLabel="Recovery Hub"
              />

              <div className="wh-mood-tabs-container">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    className={`wh-mood-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    <FontAwesomeIcon icon={tab.icon} />
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="wh-mood-tab-content">
                {activeTab === 'checkin' && renderCheckinTab()}
                {activeTab === 'history' && renderHistoryTab()}
                {activeTab === 'resources' && renderResourcesTab()}
              </div>
            </>
          )}
        </Container>
      </section>
    </div>
  );
};

export default PostpartumMood;
